import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus, Search, ChevronRight, User, Car, Wrench, X,
  AlertTriangle, ClipboardCheck, FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import { STATUS_META, remapStatus, inr } from "./lib";

// After the Feb 2026 8-state simplification, a JC is billable once it enters
// QC (i.e. the mechanic has finished the work) and stays billable through
// ready-for-delivery and closed. `remapStatus` folds legacy statuses first.
const BILLABLE_STATUSES = new Set([
  "quality_check", "ready_for_delivery", "closed",
]);

const TERMINAL_STATUSES = new Set(["closed", "cancelled"]);

const SAVED_VIEWS = [
  { key: "",                    label: "All",                 icon: null },
  { key: "active",              label: "Active",              icon: Wrench },
  { key: "my_jobs",             label: "My Jobs",             icon: User },
  { key: "due_today",           label: "Due Today",           icon: ClipboardCheck },
  { key: "delayed",             label: "Delayed",             icon: AlertTriangle },
  { key: "approval_pending",    label: "Awaiting Approval",   icon: null },
  { key: "ready_for_delivery",  label: "Ready for Delivery",  icon: null },
  { key: "insurance",           label: "Insurance",           icon: null },
  { key: "closed",              label: "Closed",              icon: null },
];

/**
 * Enhanced list of job cards with saved views, filters, and status chips.
 * Matches spec Section 32.
 */
export default function JobCardsList() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");
  const [view, setView] = useState(sp.get("view") || "");
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    // Views mapped to backend filters where possible
    if (view === "approval_pending") params.status = "approval_pending";
    else if (view === "ready_for_delivery") params.status = "ready_for_delivery";
    else if (view === "closed") params.status = "closed";
    if (q.trim()) params.q = q.trim();
    api.get("/job-cards", { params })
      .then((r) => setRows(r.data || []))
      .catch((e) => setError(e.response?.data?.detail || "Could not load job cards"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
     
  }, [q, view]);

  // sync URL with view state
  useEffect(() => {
    const next = new URLSearchParams(sp);
    if (view) next.set("view", view); else next.delete("view");
    setSp(next, { replace: true });
     
  }, [view]);

  const filtered = useMemo(() => {
    let out = rows;
    const today = new Date().toISOString().slice(0, 10);
    if (view === "active") {
      out = out.filter((r) => !TERMINAL_STATUSES.has(remapStatus(r.status)));
    } else if (view === "my_jobs" && me) {
      out = out.filter((r) => r.service_advisor_id === me.id);
    } else if (view === "due_today") {
      out = out.filter((r) => (r.promised_at || "").startsWith(today));
    } else if (view === "delayed") {
      const now = Date.now();
      out = out.filter((r) => {
        if (TERMINAL_STATUSES.has(remapStatus(r.status))) return false;
        return r.promised_at && new Date(r.promised_at).getTime() < now;
      });
    } else if (view === "insurance") {
      out = out.filter((r) => r.repair_type === "insurance");
    }
    return out;
  }, [rows, view, me]);

  const totals = useMemo(() => {
    const t = { total: rows.length, active: 0, delayed: 0, ready: 0 };
    const now = Date.now();
    for (const r of rows) {
      const st = remapStatus(r.status);
      if (!TERMINAL_STATUSES.has(st)) t.active++;
      if (st === "ready_for_delivery") t.ready++;
      if (r.promised_at && new Date(r.promised_at).getTime() < now
          && !TERMINAL_STATUSES.has(st)) t.delayed++;
    }
    return t;
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="jobcards-list-page">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h1 className="text-[24px] md:text-[28px] font-bold text-[#1d3557] leading-tight">
            Job Cards
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            Every vehicle currently moving through your workshop.
          </p>
        </div>
        <Link
          to="/job-cards/new"
          data-testid="jc-list-create-btn"
          className="h-11 px-4 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[13px] font-semibold inline-flex items-center gap-2 self-start"
        >
          <Plus size={16} /> Create Job Card
        </Link>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-3 md:grid-cols-3 gap-3">
        <Kpi label="Total" value={totals.total} testId="jc-kpi-total" />
        <Kpi label="Active" value={totals.active} tone="blue" testId="jc-kpi-active" />
        <Kpi label="Ready for Delivery" value={totals.ready} tone="green" testId="jc-kpi-ready" />
      </div>

      {/* Views + search */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex flex-wrap gap-1 bg-white rounded-lg border border-[#E4E7EC] p-1 overflow-x-auto">
          {SAVED_VIEWS.map((v) => (
            <button
              key={v.key || "all"}
              onClick={() => setView(v.key)}
              data-testid={`jc-view-${v.key || "all"}`}
              className={`h-8 px-3 rounded-md text-[11.5px] font-semibold whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
                view === v.key ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
              }`}
            >
              {v.icon && <v.icon size={11} />}
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="jc-list-search"
            placeholder="Search by job number, complaint, customer or vehicle…"
            className="w-full h-10 pl-9 pr-9 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8dadc] hover:text-[#e63946]"
              type="button"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading…</div>
        ) : error ? (
          <div className="p-10 text-center text-[13px] text-[#e63946]">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center" data-testid="jc-list-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <Wrench size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No job cards match this view</div>
            <p className="text-[12px] text-[#667085] mt-1">Create a job card to start tracking work.</p>
            <Link
              to="/job-cards/new"
              className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold"
            >
              <Plus size={14} /> Create Job Card
            </Link>
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="jc-list-desktop-table">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                    <th className="px-5 py-2.5">Job Card</th>
                    <th className="px-3 py-2.5">Vehicle</th>
                    <th className="px-3 py-2.5">Customer</th>
                    <th className="px-3 py-2.5">Service</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Advisor</th>
                    <th className="px-3 py-2.5">Expected</th>
                    <th className="px-5 py-2.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <Row key={r.id} r={r} onOpen={() => nav(`/job-cards/${r.id}`)} />
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile */}
            <ul className="md:hidden divide-y divide-[#F1FAEE]" data-testid="jc-list-mobile">
              {filtered.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/job-cards/${r.id}`}
                    className="block p-4 hover:bg-[#F7F9FC]"
                    data-testid={`jc-card-${r.number || r.job_number}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[13px] font-semibold text-[#1d3557]">
                        {r.number || r.job_number}
                      </span>
                      <StatusChip status={r.status} />
                    </div>
                    <div className="text-[13px] font-medium text-[#1d3557] truncate">
                      {r.customer?.name || r.customer_snapshot?.name || "—"}
                    </div>
                    <div className="text-[11.5px] text-[#667085] truncate">
                      <Car size={10} className="inline mr-1" />
                      {r.vehicle?.reg_number || r.vehicle_snapshot?.reg_number || "—"} · {r.service_type || "—"}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

function Row({ r, onOpen }) {
  const nav = useNavigate();
  const billable = BILLABLE_STATUSES.has(remapStatus(r.status));
  return (
    <tr
      className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer"
      onClick={onOpen}
      data-testid={`jc-row-${r.number || r.job_number}`}
    >
      <td className="px-5 py-3">
        <div className="font-mono text-[12.5px] font-semibold text-[#1d3557]">
          {r.number || r.job_number}
        </div>
        <div className="text-[10.5px] text-[#667085]">
          {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="font-mono text-[12px] text-[#1d3557]">
          {r.vehicle?.reg_number || r.vehicle_snapshot?.reg_number || "—"}
        </div>
        <div className="text-[11px] text-[#667085] truncate max-w-[140px]">
          {[r.vehicle?.make || r.vehicle_snapshot?.make, r.vehicle?.model || r.vehicle_snapshot?.model].filter(Boolean).join(" ")}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="font-medium text-[#1d3557]">
          {r.customer?.name || r.customer_snapshot?.name || "—"}
        </div>
        <div className="text-[11px] text-[#667085]">
          {r.customer?.phone || r.customer_snapshot?.phone || ""}
        </div>
      </td>
      <td className="px-3 py-3 text-[#667085] max-w-[180px] truncate">{r.service_type || "—"}</td>
      <td className="px-3 py-3">
        <StatusChip status={r.status} />
      </td>
      <td className="px-3 py-3 text-[12px] text-[#667085]">{r.service_advisor_name || "—"}</td>
      <td className="px-3 py-3 text-[12px] text-[#667085]">
        {r.promised_at ? new Date(r.promised_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
      </td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex items-center gap-1.5 justify-end">
          {billable && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); nav(`/invoices/new?job_card=${r.id}`); }}
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[11px] font-semibold"
              data-testid={`jc-row-bill-${r.number || r.job_number}`}
              title="Convert this job card to an invoice"
            >
              <FileText size={11} /> Bill
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className="inline-flex items-center gap-1 h-7 px-3 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE]"
            data-testid={`jc-row-open-${r.number || r.job_number}`}
          >
            Open <ChevronRight size={11} />
          </button>
        </div>
      </td>
    </tr>
  );
}

export function StatusChip({ status }) {
  const s = remapStatus(status);
  const meta = STATUS_META[s] || STATUS_META.draft;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${meta.chip}`}
      data-testid={`jc-status-${s}`}
    >
      {meta.label}
    </span>
  );
}

function Kpi({ label, value, tone = "steel", testId }) {
  const toneMap = {
    steel: "bg-white border-[#E4E7EC]",
    blue:  "bg-[#a8dadc]/20 border-[#a8dadc]",
    red:   "bg-[#e63946]/8 border-[#e63946]/25",
    amber: "bg-[#FEF0C7] border-[#F79009]/25",
    green: "bg-[#F1FAEE] border-[#12B76A]/20",
  };
  return (
    <div className={`rounded-xl border p-4 ${toneMap[tone]}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#667085]">{label}</div>
      <div className="text-[26px] font-bold text-[#1d3557] font-mono tabular-nums mt-1">{value}</div>
    </div>
  );
}
